import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { ResponsiveLine } from '@nivo/line';
import { observer } from 'mobx-react-lite';
import { CartesianMarkerProps, linearGradientDef } from '@nivo/core';
import { useTheme } from 'styled-components';
import { formatDecimal } from '@app/common';
import { TotalValueChartTooltip } from '../elements/total-value-chart-tooltip';
import { InlineLegend } from '../legend/inline/inline-legend';
import { ChartLineColorsType, ChartLineColorType, ChartLineType } from '../../../../../theme/type/chart-type';
import { useRootStore } from '../../../../../providers/root-store-provider';
import { AreaChartDataMobxDto } from '../../../../../models/mobx/dtos/statistic/area-chart-data-mobx-dto';
import { AreaChartMobxDto } from '../../../../../models/mobx/dtos/statistic/area-chart-mobx-dto';
import { IconZoom } from '../../../icons/zoom';
import { ChartDataEnum } from '../../../../../defs/api';

export const BaseLineChart: React.FC<ChartLineType> = observer(
    ({
        tab,
        chartData,
        unit,
        dataOverride,
        dateFormat,
        labelDateFormat,
        filter,
        onChange,
        markers,
        isFullScreen,
        toggleFullScreen,
        showFullScreenTrigger = true,
        colorOverrides,
        seriesLabels,
        axisBottomTickValues,
        axisLeftMinMargin = 95,
        controls,
        legendLabels,
        legendTooltips,
    }) => {
        const theme = useTheme();
        const chartGradientPrefixRef = useRef(`chart-gradient-${Math.random().toString(36).slice(2, 10)}`);
        const chartGradientPrefix = chartGradientPrefixRef.current;
        const fullScreenTriggerStyle: React.CSSProperties = {
            backgroundColor: theme.palette.color.secondary.opacity.low,
            transition: `all ${theme.base.transition}`,
            color: theme.palette.common.textInverse,
        };
        const [windowWidth, setWindowWidth] = useState(window.innerWidth);
        const { privateStore, portfolioStore } = useRootStore();

        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        useEffect(() => {
            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }, []);

        const colors: ChartLineColorType[] = [
            {
                color: 'primary',
                type: 'light',
            },
            {
                color: 'secondary',
                type: 'main',
            },
            {
                color: 'negative',
                type: 'main',
            },
            {
                color: 'green',
                type: 'main',
            },
            {
                color: 'bitcoin',
                type: 'main',
            },
        ];

        const graphColors: ChartLineColorsType = {
            totalValue: theme.palette.color[colors[0].color][colors[0].type] as string,
            closeValue: theme.palette.color[colors[0].color][colors[0].type] as string,
            closePercentage: theme.palette.color[colors[0].color][colors[0].type] as string,
            invested: theme.palette.color[colors[1].color][colors[1].type] as string,
            profitLoss: theme.palette.color[colors[1].color][colors[1].type] as string,
            CSPX: theme.palette.color[colors[2].color][colors[2].type] as string,
            CNDX: theme.palette.color[colors[3].color][colors[3].type] as string,
            'BTC-USD': theme.palette.color[colors[4].color][colors[4].type] as string,
            personalAsset: theme.palette.color[colors[0].color][colors[0].type] as string,
            investownAsset: theme.palette.color[colors[0].color][colors[0].type] as string,
            investownCashAsset: theme.palette.color[colors[3].color][colors[3].type] as string,
            totalAllAssets: theme.palette.color[colors[0].color][colors[0].type] as string,
            averageReturn: theme.palette.color.positive.main,
            profitLoss_twr: theme.palette.color.primary.main,
            CSPX_twr: theme.palette.color[colors[2].color][colors[2].type] as string,
            CNDX_twr: theme.palette.color[colors[3].color][colors[3].type] as string,
            'BTC-USD_twr': theme.palette.color[colors[4].color][colors[4].type] as string,
        };

        const customChartData: AreaChartMobxDto[] = chartData.map((d: AreaChartMobxDto) => {
            if (d.chartDataType === ChartDataEnum.PinnedPortfolioValue) {
                return {
                    ...d,
                    color: theme.assetColor[
                        portfolioStore.pinnedPortfolios.find((pp) => pp.name === d.id)?.colorName || 'gray'
                    ].main as string,
                };
            }

            return {
                ...d,
                color: colorOverrides?.[d.id] ?? graphColors[d.id as keyof typeof graphColors],
            };
        });

        if (!customChartData[0]) return null;

        const fill: { match: { id: string }; id: string }[] = [];
        let lowestValue = Number(
            Math.min(
                ...customChartData[0].data.map((item: AreaChartDataMobxDto) => {
                    return item.y;
                }),
            ),
        );

        customChartData.forEach((data) => {
            const safeId = data.id.replace(/\s+/g, '-');
            fill.push({ match: { id: data.id }, id: `${chartGradientPrefix}-gradient-${safeId}` });
            if (filter.get(data.id)) {
                const currentLowestValue = Number(
                    Math.min(
                        ...data.data.map((item: AreaChartDataMobxDto) => {
                            return item.y;
                        }),
                    ),
                );

                if (currentLowestValue < lowestValue) {
                    lowestValue = currentLowestValue;
                }
            }
        });

        const markerData: CartesianMarkerProps[] | undefined = useMemo(() => {
            if (markers) {
                return markers.map((marker) => {
                    return {
                        axis: marker.axis,
                        value: marker.value,
                        legend: marker.label,
                        legendOrientation: marker.legendOrientation,
                        legendPosition: marker.legendPosition,
                        lineStyle: {
                            strokeWidth: 2,
                            strokeDasharray: '4 4',
                        },
                        textStyle: { fill: theme.palette.common.textMuted },
                    };
                });
            }
            return undefined;
        }, [markers]);

        const xTickValues = Math.floor(windowWidth / 180);

        const dynamicLeftMargin = useMemo(() => {
            if (privateStore.isPrivateModeEnabled) return 20;

            const activeData = dataOverride ? customChartData : customChartData.filter((data) => filter.get(data.id));
            const maxValue = Math.max(...activeData.flatMap((data) => data.data.map((item) => Math.abs(item.y))));

            const formattedMaxValue = `${Math.round(maxValue).toLocaleString('cs-CZ')} ${unit}`;
            const estimatedWidth = formattedMaxValue.length * 7;

            return Math.max(axisLeftMinMargin, Math.min(estimatedWidth, 150));
        }, [axisLeftMinMargin, customChartData, dataOverride, filter, unit, privateStore.isPrivateModeEnabled]);

        const memoBaseLine = useMemo(() => {
            const gradientDefs = customChartData.map((data) => {
                const safeId = data.id.replace(/\s+/g, '-');
                const isSolidAreaSeries =
                    data.chartDataType === ChartDataEnum.PinnedPortfolioValue ||
                    [
                        'totalValue',
                        'personalAsset',
                        'totalAllAssets',
                        'closeValue',
                        'closePercentage',
                        'averageReturn',
                    ].includes(data.id);
                const isColorOverrideSeries = Boolean(colorOverrides?.[data.id]);
                const startOpacity = isSolidAreaSeries || isColorOverrideSeries ? 0.4 : 0.1;
                const endOffset = isSolidAreaSeries || isColorOverrideSeries ? 100 : 50;

                return linearGradientDef(`${chartGradientPrefix}-gradient-${safeId}`, [
                    { offset: 0, color: data.color, opacity: startOpacity },
                    { offset: endOffset, color: data.color, opacity: 0 },
                ]);
            });

            return (
                <ResponsiveLine
                    pointLabelYOffset={0}
                    colors={{ datum: 'color' }}
                    data={dataOverride ? customChartData : customChartData.filter((data) => filter.get(data.id))}
                    margin={{ top: 30, right: 0, bottom: 50, left: dynamicLeftMargin }}
                    markers={markerData}
                    useMesh={true}
                    xScale={{ type: 'time', format: '%D.%M.%Y' }}
                    yScale={{
                        type: 'linear',
                        min: 'auto',
                        max: 'auto',
                        stacked: false,
                        reverse: false,
                        nice: true,
                    }}
                    yFormat={(value) => formatDecimal(Number(value))}
                    xFormat={'time:%D.%M.%Y'}
                    areaBaselineValue={lowestValue}
                    tooltip={({ point }) => {
                        const formattedValue = point.data.y.toLocaleString('cs-CZ', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                        });

                        const displayValue = `${formattedValue} ${unit}`;
                        const formattedDate = moment(point.data.x).format(labelDateFormat);
                        const seriesLabel = seriesLabels?.[point.serieId as string];
                        const label = seriesLabel ? `${seriesLabel} · ${formattedDate}` : formattedDate;

                        return <TotalValueChartTooltip label={label} value={displayValue} />;
                    }}
                    curve="linear"
                    axisTop={null}
                    enableArea={true}
                    areaOpacity={1}
                    crosshairType="bottom"
                    enableGridX={false}
                    defs={gradientDefs}
                    fill={fill}
                    gridYValues={4}
                    axisBottom={{
                        tickSize: 0,
                        tickPadding: 10,
                        tickRotation: 0,
                        tickValues: axisBottomTickValues ?? xTickValues,
                        format: (value) => moment(value).locale('cs').format(dateFormat),
                    }}
                    axisLeft={
                        !privateStore.isPrivateModeEnabled
                            ? {
                                  tickSize: 0,
                                  tickPadding: 10,
                                  tickValues: 4,
                                  tickRotation: 0,
                                  format: (value) => `${Number(value).toLocaleString('cs-CZ')} ${unit}`,
                              }
                            : null
                    }
                    pointSize={0}
                    theme={{
                        axis: {
                            ticks: {
                                text: {
                                    fontSize: theme.text.size.vsmall.fontSize,
                                    fill: theme.palette.common.textMuted,
                                },
                            },
                            legend: {
                                text: {
                                    fontSize: theme.text.size.vsmall.fontSize,
                                    fill: theme.palette.common.textMuted,
                                },
                            },
                        },
                        legends: {
                            text: {
                                fontSize: theme.text.size.vsmall.fontSize,
                                fill: theme.palette.common.textMuted,
                            },
                        },
                        grid: {
                            line: {
                                stroke: theme.palette.common.border,
                                strokeWidth: 1,
                            },
                        },
                        markers: {
                            lineColor: theme.palette.common.textMuted,
                        },
                    }}
                />
            );
        }, [
            axisBottomTickValues,
            colorOverrides,
            controls,
            legendLabels,
            customChartData,
            dataOverride,
            dateFormat,
            dynamicLeftMargin,
            fill,
            filter,
            graphColors,
            isFullScreen,
            labelDateFormat,
            markerData,
            seriesLabels,
            showFullScreenTrigger,
            theme,
            toggleFullScreen,
            unit,
            xTickValues,
            lowestValue,
            privateStore.isPrivateModeEnabled,
            chartGradientPrefix,
        ]);

        return (
            <div>
                {tab && onChange && (
                    <InlineLegend
                        tab={tab}
                        data={customChartData}
                        onChange={onChange}
                        filter={filter}
                        controls={controls}
                        labels={legendLabels}
                        tooltips={legendTooltips}
                    />
                )}
                <div>
                    <div
                        className="relative"
                        style={{ height: isFullScreen ? 'calc(100vh - 12rem - 4rem - 1.8rem)' : '45rem' }}
                    >
                        {showFullScreenTrigger && !isFullScreen ? (
                            <div
                                onClick={toggleFullScreen}
                                className="absolute right-[2rem] top-[2rem] z-[500] flex h-[4rem] w-[4rem] cursor-pointer items-center justify-center rounded-full hover:bg-brand-secondary [&_i]:text-text-inverse"
                                style={fullScreenTriggerStyle}
                            >
                                <IconZoom />
                            </div>
                        ) : null}
                        {memoBaseLine}
                    </div>
                </div>
            </div>
        );
    },
);
